import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber, isValidPhone } from "../../utils/formatters";
import { showError } from "../../utils/alerts";
import { ChevronRight } from "lucide-react";
import Input from "../../components/ui/Input";
import FormButton from "../../components/ui/Form/FormButton";
import FormHeader from "../../components/ui/Form/FormHeader";
import AuthLayout from "../../components/layout/AuthLayout";
import AuthService from "../../services/Auth/auth.service";
import * as z from "zod";
import "./style.scss";

const RegisterSchema = z
  .object({
    name: z.string().min(1, "Campo obrigatório"),
    lastName: z.string(),
    role: z.string().min(1, "Campo obrigatório"),
    phone: z
      .string()
      .min(1, "Campo obrigatório")
      .regex(/^\(?\d{2}\)?\s?9\d{4}-?\d{4}$/, "Telefone inválido")
      .refine(isValidPhone, "Telefone inválido"),
    email: z.email("Formato de e-mail inválido").min(1, "Campo obrigatório"),
    password: z
      .string()
      .min(1, "Campo obrigatório")
      .min(6, "Mínimo de 6 caracteres"),
    confirmPassword: z.string().min(1, "Campo obrigatório"),
    code: z
      .string()
      .min(1, "Campo obrigatório")
      .min(6, "Mínimo de 6 caracteres"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não conferem",
    path: ["confirmPassword"],
  });

export default function Register() {
  const navigate = useNavigate();
  const inputNameRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    lastName: "",
    role: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    code: "",
  });

  const onBackToLogin = () => navigate("/login");

  const handleRegister = async (e) => {
    e.preventDefault();
    const form = RegisterSchema.safeParse(userData);

    if (!form.success) {
      const fieldErrors = {};

      form.error.issues.forEach((issue) => {
        const field = issue.path[0];
        fieldErrors[field] = issue.message;
      });

      setErrors(fieldErrors);
    } else {
      try {
        setIsLoading(true);
        await AuthService.registerUser(form.data);
        navigate("/verify-email");
      } catch (error) {
        showError(error);
      } finally {
        setIsLoading(false);
        setErrors({});
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    if (name === "phone") {
      formattedValue = formatPhoneNumber(value);
    }

    if (name === "code") {
      formattedValue = value.toUpperCase();
    }

    setUserData((prev) => ({ ...prev, [name]: formattedValue }));
    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  useEffect(() => {
    inputNameRef.current?.focus();
  }, []);

  return (
    <AuthLayout
      panelTitle="Sua jornada CX começa em um voucher."
      panelDescription="O voucher libera o acesso à sua cooperativa dentro da plataforma 121 Insights CX. A partir dele, você passa a acompanhar os indicadores de experiência do beneficiário, os drivers identificados pela IA Eva e os planos de ação compartilhados com o Sistema Unimed."
    >
      <form className="register__form" onSubmit={handleRegister} noValidate>
        <FormHeader
          title="Crie sua conta."
          description="Comece a transformar a experiência do beneficiário da sua
            cooperativa."
        />

        <div className="register__grid register__grid--2">
          <Input
            ref={inputNameRef}
            name="name"
            label="Nome"
            placeholder="Digite seu nome"
            value={userData.name}
            onChange={handleChange}
            error={errors.name}
            required
          />

          <Input
            name="lastName"
            label="Sobrenome"
            placeholder="Digite seu sobrenome"
            value={userData.lastName}
            onChange={handleChange}
            error={errors.lastName}
          />
        </div>

        <Input
          name="role"
          label="Cargo"
          placeholder="Informe o seu cargo"
          value={userData.role}
          onChange={handleChange}
          error={errors.role}
          required
        />

        <div className="register__grid register__grid--2">
          <Input
            name="phone"
            label="Telefone (celular)"
            placeholder="Ex: (00) 91234-5678"
            value={userData.phone}
            type="tel"
            onChange={handleChange}
            error={errors.phone}
            maxLength={15}
            required
          />

          <Input
            name="email"
            label="E-mail corporativo"
            placeholder="email@email.com"
            value={userData.email}
            type="email"
            onChange={handleChange}
            error={errors.email}
            required
          />
        </div>

        <div className="register__grid register__grid--2">
          <Input
            name="password"
            label="Senha"
            placeholder="••••••"
            type={showPassword ? "text" : "password"}
            value={userData.password}
            onChange={handleChange}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            error={errors.password}
            required
          />

          <Input
            name="confirmPassword"
            label="Confirmar senha"
            placeholder="••••••"
            type={showConfirmPassword ? "text" : "password"}
            value={userData.confirmPassword}
            onChange={handleChange}
            showPassword={showConfirmPassword}
            setShowPassword={setShowConfirmPassword}
            error={errors.confirmPassword}
            required
          />
        </div>

        <Input
          name="code"
          label="Voucher"
          placeholder="Digite o código do voucher"
          value={userData.code}
          onChange={handleChange}
          error={errors.code}
          required
        />

        <FormButton
          text="Criar conta"
          isLoading={isLoading}
          loadingText="Criando conta..."
          icon={<ChevronRight size={16} strokeWidth={2.5} />}
        />

        <div className="auth__footer-link">
          Já tem uma conta? <button onClick={onBackToLogin}>Entrar</button>
        </div>
      </form>
    </AuthLayout>
  );
}
